// src/scenes/login/index.jsx
import React, { useState, useEffect } from 'react';
import {
    Button,
    Typography,
    TextField,
    IconButton,
    InputAdornment,
    Alert
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../authContext/AuthContext';
import { useNavigate } from 'react-router-dom';

const collierLogo = "https://www.collierschools.com/cms/lib/FL01903251/Centricity/Template/GlobalAssets/images///logos/collier-logo.png";

const Login = () => {
    const { login } = useAuth();
    const navigate = useNavigate();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [users, setUsers] = useState([]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await fetch('https://azuresql.swancreate.com/api/swa/ccps_users');
                if (!response.ok) throw new Error('Failed to fetch users');
                const data = await response.json();
                setUsers(data);
            } catch (err) {
                console.error(err.message);
                setError('Failed to load user data. Please try again later.');
            }
        };
        fetchUsers();
    }, []);

    const handleLogin = () => {
        setError('');
        const foundUser = users.find(
            (user) =>
                user.email.toLowerCase() === username.toLowerCase() &&
                user.password === password &&
                user.isActive
        );

        if (foundUser) {
            // Allow RoleID 1 to login, other roles must have department_id equal to 3
            if (foundUser.RoleID !== 1 && foundUser.department_id !== 3) {
                setError('Access denied: Only Role 1 or users from DepartmentID 3 can access this site.');
                return;
            }
            login({
                apiid: foundUser.apiid,
                userid: foundUser.userid,
                username: `${foundUser.FirstName} ${foundUser.LastName}`,
                email: foundUser.email,
                role: foundUser.Role,
                roleId: foundUser.RoleID,
                departmentId: foundUser.department_id,
                departmentName: foundUser.DepartmentName,
                isActive: foundUser.isActive,
            });
            navigate("/");
        } else {
            setError('Invalid username or password. Please try again.');
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') handleLogin();
    };

    return (
        <div style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: '#001f3f', // Outer container dark background
            padding: '16px',
            textAlign: 'center',
        }}>
            <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundImage: `url(${collierLogo})`,
                backgroundSize: '480px',
                backgroundRepeat: 'repeat',
                opacity: 0.25,
                zIndex: 0,
            }} />
            <div style={{
                padding: '32px',
                borderRadius: '16px',
                backgroundColor: '#2c2c2c', // Inner container dark background
                maxWidth: '400px',
                zIndex: 1,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            }}>
                <img src={collierLogo} alt="Collier Logo" style={{ width: '150px', marginBottom: '16px' }} />
                <Typography variant="h4" style={{ color: '#fff' }} gutterBottom>CCPS Login</Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <TextField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyPress={handleKeyPress}
                    sx={{
                        my: 2,
                        bgcolor: '#444',
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': { borderRadius: '8px', color: '#fff' },
                        '& .MuiInputLabel-root': { color: '#fff' }
                    }}
                />

                <TextField
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    sx={{
                        my: 2,
                        bgcolor: '#444',
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': { borderRadius: '8px', color: '#fff' },
                        '& .MuiInputLabel-root': { color: '#fff' }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(p => !p)}>
                                    {showPassword ? <VisibilityOff style={{ color: '#fff' }} /> : <Visibility style={{ color: '#fff' }} />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <Button
                    variant="contained"
                    fullWidth
                    sx={{ bgcolor: '#84BD00', ':hover': { bgcolor: '#C8DA30' } }}
                    onClick={handleLogin}>
                    Login
                </Button>
            </div>
        </div>
    );
};

export default Login;
